import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { SubmitHandler } from "react-hook-form";
import { useEffect } from "react";

import { language } from "@chef/constants";
import {
  useGetCioAttributesQuery,
  useSetCioAttributeMutation,
} from "@chef/state-management";
import {
  useConfirmDialog,
  useExtendedForm,
  useNavigationIntercept,
} from "@chef/hooks";
import { LeaveWithoutSavingModal } from "@chef/smart-components";
import { Button, Card, Checkbox } from "@chef/components";

import { sendSuccessNotification } from "../utils";

import { intl } from "./CommunicationPreferencesForm.Intl";

interface ContactPreferences {
  email: boolean;
  sms: boolean;
  phone: boolean;
  cutOffNotice: boolean;
  socialMedia: boolean;
  print: boolean;
}

const schema = yup.object().shape({
  email: yup.boolean(),
  sms: yup.boolean(),
  phone: yup.boolean(),
  cutOffNotice: yup.boolean(),
  socialMedia: yup.boolean(),
  print: yup.boolean(),
});

const mapTopicsToPreferences = (
  topics: Record<string, boolean>,
): ContactPreferences => {
  return {
    email: topics["topic_1"] === false ? false : true,
    sms: topics["topic_2"] === false ? false : true,
    phone: topics["topic_3"] === false ? false : true,
    cutOffNotice: topics["topic_4"] === false ? false : true,
    socialMedia: topics["topic_5"] === false ? false : true,
    print: topics["topic_6"] === false ? false : true,
  };
};

const mapPreferencesToTopics = (
  preferences: ContactPreferences,
): Record<string, boolean> => {
  return {
    topic_1: preferences.email,
    topic_2: preferences.sms,
    topic_3: preferences.phone,
    topic_4: preferences.cutOffNotice,
    topic_5: preferences.socialMedia,
    topic_6: preferences.print,
  };
};

export const CommunicationPreferencesForm = () => {
  const { data: getCioAttributesQuery } = useGetCioAttributesQuery();
  const [setCioAttribute] = useSetCioAttributeMutation();

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, isSubmitting },
  } = useExtendedForm<ContactPreferences>({
    resolver: yupResolver(schema),
    mode: "all",
    defaultValues: {
      email: true,
      sms: true,
      phone: true,
      cutOffNotice: true,
      socialMedia: true,
      print: true,
    },
  });

  const onSubmit: SubmitHandler<ContactPreferences> = async ({
    email,
    sms,
    phone,
    cutOffNotice,
    socialMedia,
    print,
  }) => {
    if (
      getCioAttributesQuery?.customer?.unsubscribed &&
      (email || sms || phone || cutOffNotice || socialMedia || print)
    ) {
      await setCioAttribute({
        key: "unsubscribed",
        value: false,
      });
    }
    await setCioAttribute({
      key: "cio_subscription_preferences",
      value: {
        topics: mapPreferencesToTopics({
          email,
          sms,
          phone,
          cutOffNotice,
          socialMedia,
          print,
        }),
      },
    }).unwrap();

    sendSuccessNotification(intl.YOUR_SETTINGS_HAVE_BEEN_UPDATED);
  };

  const { handleCancel } = useConfirmDialog();

  const shouldInterceptLeave = isDirty;
  const { intercepting, replyIntercept } = useNavigationIntercept({
    shouldIntercept: () => {
      return shouldInterceptLeave;
    },
  });

  useEffect(() => {
    if (!getCioAttributesQuery?.customer) {
      return;
    }

    const customer = getCioAttributesQuery.customer;

    if (customer.unsubscribed) {
      reset({
        email: false,
        sms: false,
        phone: false,
        cutOffNotice: false,
        socialMedia: false,
        print: false,
      });

      return;
    }

    try {
      const cioSubscriptionPreferences: { topics: Record<string, boolean> } =
        JSON.parse(
          customer.attributes["cio_subscription_preferences"] as string,
        );

      reset(mapTopicsToPreferences(cioSubscriptionPreferences.topics));
    } catch (error) {
      console.error(error);
    }
  }, [getCioAttributesQuery, reset]);

  const showPrintCheckbox = ["no", "se"].includes(language);

  return (
    <section>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card id="change-contact-settings-card" className="px-8">
          <div className="py-6">
            <h3 className="pb-3">
              <strong>{intl.SETTINGS}</strong>
            </h3>
            <p>{intl.HOW_WOULD_YOU_LIKE_US_TO_COMMUNICATE}</p>
          </div>

          <div className="text-sm">
            <Checkbox
              id="update-contact-settings-email-checkbox"
              {...register("email")}
            >
              <p>
                <strong>{intl.VIA_EMAIL}</strong>
              </p>
              <p>{intl.I_WOULD_LIKE_TO_RECEIVE_VIA_EMAIL}</p>
            </Checkbox>

            <hr className="m-5 text-grey-3" />

            <Checkbox
              {...register("sms")}
              id="update-contact-settings-sms-checkbox"
            >
              <p>
                <strong>{intl.VIA_SMS}</strong>
              </p>
              <p>{intl.I_WOULD_LIKE_TO_RECEIVE_VIA_SMS}</p>
            </Checkbox>

            <hr className="m-5 text-grey-3" />

            <Checkbox
              {...register("phone")}
              id="update-contact-settings-phone-checkbox"
            >
              <p>
                <strong>{intl.VIA_PHONE}</strong>
              </p>
              <p>{intl.I_WOULD_LIKE_TO_RECEIVE_VIA_PHONE}</p>
            </Checkbox>

            <hr className="m-5 text-grey-3" />

            <Checkbox
              {...register("socialMedia")}
              id="update-contact-settings-social-media-checkbox"
            >
              <p>
                <strong>{intl.VIA_SOCIAL_MEDIA}</strong>
              </p>
              <p>{intl.I_WOULD_LIKE_TO_RECEIVE_VIA_SOCIAL_MEDIA}</p>
            </Checkbox>

            {showPrintCheckbox && (
              <>
                <hr className="m-5 text-grey-3" />

                <Checkbox
                  {...register("print")}
                  id="update-contact-settings-print-checkbox"
                >
                  <p>
                    <strong>{intl.VIA_PRINT}</strong>
                  </p>
                  <p>{intl.I_WOULD_LIKE_TO_RECEIVE_VIA_PRINT}</p>
                </Checkbox>
              </>
            )}

            <hr className="m-5 text-grey-3" />

            <Checkbox
              {...register("cutOffNotice")}
              id="update-contact-settings-reminder-checkbox"
            >
              <p>
                <strong>{intl.REMINDERS}</strong>
              </p>
              <p>{intl.WILL_BE_NOTIFIED_BEFORE_DEADLINE}</p>
            </Checkbox>
          </div>

          <div className="flex flex-col gap-4 mt-10 lg:flex-row lg:justify-end">
            <Button
              disabled={!isDirty}
              primary
              type="submit"
              id="update-contact-settings-button"
              loading={isSubmitting}
            >
              {intl.SAVE_BUTTON_TEXT}
            </Button>
          </div>
        </Card>
      </form>
      <LeaveWithoutSavingModal
        show={intercepting}
        onClose={() => {
          replyIntercept(false);
          handleCancel();
        }}
        onConfirm={() => replyIntercept(true)}
      />
    </section>
  );
};
